import React, { useState, useEffect } from 'react';
import {
  TextField,
  FormControl,
  FormLabel,
  FormHelperText,
  InputAdornment
} from '@mui/material';

const CustomNumberInput = ({
  label,
  value,
  onChange,
  placeholder = "Enter number...",
  disabled = false,
  required = false,
  error = false,
  helperText,
  description,
  size = "medium",
  variant = "outlined",
  fullWidth = true,
  allowNegativeValue = false,
  min,
  max,
  step,
  decimalPlaces,
  suffix,
  prefix,
  ...props
}) => {
  const [displayValue, setDisplayValue] = useState('');

  // Convert value to display string
  useEffect(() => {
    if (value === null || value === undefined || value === '') {
      setDisplayValue('');
    } else {
      let numValue = Number(value);
      if (!isNaN(numValue)) {
        // Apply decimal places if specified
        if (decimalPlaces !== undefined) {
          numValue = Number(numValue.toFixed(decimalPlaces));
        }
        setDisplayValue(numValue.toString());
      } else {
        setDisplayValue('');
      }
    }
  }, [value, decimalPlaces]);

  // Handle input change
  const handleInputChange = (event) => {
    const inputValue = event.target.value;
    
    // Allow empty string
    if (inputValue === '') {
      setDisplayValue('');
      onChange('');
      return;
    }

    // Remove any non-numeric characters except decimal point and minus sign
    let cleanValue = inputValue.replace(/[^0-9.-]/g, '');
    
    // Handle negative values
    if (!allowNegativeValue) {
      cleanValue = cleanValue.replace(/-/g, '');
    } else {
      // Only allow minus sign at the beginning
      cleanValue = cleanValue.replace(/^-+/, '-');
      if (cleanValue.includes('-') && cleanValue.indexOf('-') !== 0) {
        cleanValue = cleanValue.replace(/-/g, '');
      }
    }

    // Handle decimal point - only allow one
    const decimalCount = (cleanValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const firstDecimalIndex = cleanValue.indexOf('.');
      cleanValue = cleanValue.substring(0, firstDecimalIndex + 1) + 
                   cleanValue.substring(firstDecimalIndex + 1).replace(/\./g, '');
    }

    // Apply decimal places restriction
    if (decimalPlaces !== undefined && cleanValue.includes('.')) {
      const parts = cleanValue.split('.');
      if (parts[1] && parts[1].length > decimalPlaces) {
        cleanValue = parts[0] + '.' + parts[1].substring(0, decimalPlaces);
      }
    }

    setDisplayValue(cleanValue);

    // Convert to number for onChange
    if (cleanValue === '' || cleanValue === '-') {
      onChange('');
    } else {
      const numValue = Number(cleanValue);
      if (!isNaN(numValue)) {
        // Apply min/max constraints
        let finalValue = numValue;
        if (min !== undefined && numValue < min) {
          finalValue = min;
          setDisplayValue(finalValue.toString());
        } else if (max !== undefined && numValue > max) {
          finalValue = max;
          setDisplayValue(finalValue.toString());
        }
        onChange(finalValue);
      }
    }
  };

  // Handle focus - allow user to select all text for easy replacement
  const handleFocus = (event) => {
    event.target.select();
  };

  // Handle key press for additional restrictions
  const handleKeyPress = (event) => {
    const char = event.key;
    
    // Allow backspace, delete, tab, escape, enter
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter'].includes(char)) {
      return;
    }

    // Allow numbers
    if (char >= '0' && char <= '9') {
      return;
    }

    // Allow decimal point (only one)
    if (char === '.' && !displayValue.includes('.')) {
      return;
    }

    // Allow minus sign (only at beginning and if negative values are allowed)
    if (char === '-' && allowNegativeValue && displayValue === '') {
      return;
    }

    // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (event.ctrlKey && ['a', 'c', 'v', 'x'].includes(char.toLowerCase())) {
      return;
    }

    // Block all other characters
    event.preventDefault();
  };

  // Handle paste
  const handlePaste = (event) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData('text');
    
    // Clean the pasted data
    let cleanData = pasteData.replace(/[^0-9.-]/g, '');
    
    if (!allowNegativeValue) {
      cleanData = cleanData.replace(/-/g, '');
    }
    
    // Handle decimal point
    const decimalCount = (cleanData.match(/\./g) || []).length;
    if (decimalCount > 1) {
      const firstDecimalIndex = cleanData.indexOf('.');
      cleanData = cleanData.substring(0, firstDecimalIndex + 1) + 
                  cleanData.substring(firstDecimalIndex + 1).replace(/\./g, '');
    }

    // Apply decimal places restriction
    if (decimalPlaces !== undefined && cleanData.includes('.')) {
      const parts = cleanData.split('.');
      if (parts[1] && parts[1].length > decimalPlaces) {
        cleanData = parts[0] + '.' + parts[1].substring(0, decimalPlaces);
      }
    }

    // Simulate input change
    const syntheticEvent = {
      target: { value: cleanData }
    };
    handleInputChange(syntheticEvent);
  };

  return (
    <FormControl fullWidth={fullWidth} error={error} required={required}>
      <TextField
        {...props}
        fullWidth={fullWidth}
        size={size}
        variant={variant}
        label={label ? (
          required ? (
            <span>
              <span style={{ color: '#f44336' }}>*</span> {label}
            </span>
          ) : label
        ) : undefined}
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyPress={handleKeyPress}
        onPaste={handlePaste}
        placeholder={placeholder}
        disabled={disabled}
        type="text" // Use text type to have full control over input
        InputProps={{
          startAdornment: prefix ? (
            <InputAdornment position="start">{prefix}</InputAdornment>
          ) : undefined,
          endAdornment: suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : undefined,
          inputProps: {
            inputMode: 'numeric', // Show numeric keyboard on mobile
            pattern: allowNegativeValue ? '[0-9.-]*' : '[0-9.]*'
          }
        }}
      />

      {/* Helper Text and Description */}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {description && <FormHelperText>{description}</FormHelperText>}
    </FormControl>
  );
};

export default CustomNumberInput;
